/**
 * Shared timeline context: prop-drilling the same handful of values (pixels-
 * per-second, player controls, lane DOM refs, the cross-track drop target) down
 * to deeply nested ClipBlock/ClipStrip children got noisy, so they live here.
 */
import { createContext, createElement, useContext, type ReactNode } from 'react';
import type { PlayerControls } from '../EditorPage';
import type { LaneRefs } from './constants';

export interface TimelineCtx {
  pps: number;
  controls: PlayerControls;
  laneRefs: LaneRefs;
  /** lane a dragged clip would land on (same-kind, ≠ origin) — for highlighting */
  dropTrackId: string | null;
  setDropTrack: (id: string | null) => void;
}

const Ctx = createContext<TimelineCtx | null>(null);

export function TimelineProvider({
  value,
  children,
}: {
  value: TimelineCtx;
  children: ReactNode;
}) {
  return createElement(Ctx.Provider, { value }, children);
}

export function useTimelineCtx(): TimelineCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useTimelineCtx must be used inside a TimelineProvider');
  return ctx;
}
